import { useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { commonMessages } from 'app/i18n/messages'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import { ConfigFile } from 'components/Icons'
import { KebabMenu } from 'components/KebabMenu'
import { DeleteButtonCell, DownloadButtonCell } from 'components/Table'
import { currentUserSelector } from 'features/auth/login/selectors'
import { selectedHomeSelector } from 'features/home/selectors'

import {
  ConfigKebabContainer,
  ConfigMobileCardContainer,
  ConfigMobileCardLabelWrapper,
  ConfigMobileCardTitle,
  HomeConfigurationWhiteBlock,
} from '../../styles'
import type { DomainConfig } from 'features/domains/types'
import {
  HomeConfigActionCellProgressBar,
  HomeConfigFilesMobileDeployingContainer,
  HomeConfigFilesMobileProgressContainer,
  MobileHomeConfigDeployButton,
} from '../styles'
import { hasSystemAccessEnabled } from 'features/home/utils'
import { deleteDomainConfigThunk, deployDomainConfigThunk } from '../../slice'
import { homeConfigurationMessages } from '../../messages'
import { selectSoftwareUpdatesInProgress } from 'features/home/components/HomeSoftware/HomeCloudSoftwareHistory/selectors'

interface MobileDomainConfig extends DomainConfig {
  firstName?: string
  lastName?: string
  isDeployShown?: boolean
}

interface MobileHomeConfigFilesProps {
  original: MobileDomainConfig
  values: Record<string, any>
}

export function MobileHomeConfigFiles(props: MobileHomeConfigFilesProps) {
  const { original, values } = props

  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()

  const [progress, setProgress] = useState(0)

  const user = useAppSelector(currentUserSelector)
  const home = useAppSelector(selectedHomeSelector.data)
  const softwareUpdatesInProgress = useAppSelector(
    selectSoftwareUpdatesInProgress
  )

  const isUpdateInProgress = softwareUpdatesInProgress?.length > 0

  const hasSystemAccess = hasSystemAccessEnabled(home)

  const fileName = `${values.name}.tar.gz`

  const handleDelete = useCallback(async () => {
    await dispatch(deleteDomainConfigThunk(original.id, original.domainId))
  }, [dispatch, original.id, original.domainId])

  const handleDeploy = useCallback(async () => {
    if (home?.id && user?.id) {
      try {
        await dispatch(
          deployDomainConfigThunk(
            {
              config_id: original.id,
              home_id: home.id,
              integrator_id: user.id,
            },
            setProgress
          )
        )
      } catch (e) {
        throw e
      } finally {
        setProgress(0)
      }
    }
  }, [dispatch, home?.id, original.id, user.id])

  return (
    <HomeConfigurationWhiteBlock progress={progress}>
      <ConfigMobileCardContainer>
        <ConfigMobileCardTitle>
          <ConfigFile />
          <span>{values.name}</span>
        </ConfigMobileCardTitle>
        {!progress && (
          <KebabMenu>
            <ConfigKebabContainer>
              {hasSystemAccess && original.isDeployShown && (
                <MobileHomeConfigDeployButton
                  onClick={handleDeploy}
                  disabled={isUpdateInProgress}
                >
                  {formatMessage(homeConfigurationMessages.deployButton)}
                </MobileHomeConfigDeployButton>
              )}
              <DownloadButtonCell url={original.url} fileName={fileName}>
                {formatMessage(commonMessages.download)}
              </DownloadButtonCell>
              {hasSystemAccess && (
                <DeleteButtonCell
                  title={formatMessage(
                    homeConfigurationMessages.deleteFileTitle
                  )}
                  handleConfirm={handleDelete}
                  label={formatMessage(homeConfigurationMessages.deleteButton)}
                >
                  <FormattedMessage
                    {...homeConfigurationMessages.deleteConfirmText}
                    values={{
                      configName: <b>{values.name}</b>,
                      br: <br />,
                    }}
                  />
                </DeleteButtonCell>
              )}
            </ConfigKebabContainer>
          </KebabMenu>
        )}
      </ConfigMobileCardContainer>
      {progress ? (
        <HomeConfigFilesMobileDeployingContainer>
          <span>{formatMessage(homeConfigurationMessages.deploying)}</span>
          <HomeConfigFilesMobileProgressContainer>
            <HomeConfigActionCellProgressBar progress={progress} />
          </HomeConfigFilesMobileProgressContainer>
        </HomeConfigFilesMobileDeployingContainer>
      ) : (
        <ConfigMobileCardLabelWrapper>
          <span>{formatMessage(homeConfigurationMessages.headerUploaded)}</span>
          <IntlFormattedDate date={values.updated} withTime />
        </ConfigMobileCardLabelWrapper>
      )}
    </HomeConfigurationWhiteBlock>
  )
}
